'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { isLighthouseClient } from '@/lib/isLighthouse';
import { registerTaboolaInjector } from './taboolaActivation';

// Taboola publisher account (from Taboola Backstage onboarding code).
const TABOOLA_PUBLISHER = 'newindianexpress-dinamanicomnew';
const LOADER_SRC = `//cdn.taboola.com/libtrc/${TABOOLA_PUBLISHER}/loader.js`;
const LOADER_SCRIPT_ID = 'tb_loader_script';
export type TaboolaPageType = 'homepage' | 'category' | 'article';

/**
 * Injects the Taboola `loader.js` once and declares the current page type
 * (`homepage` / `category` / `article`) — this is the "head code" from the
 * Taboola onboarding snippet, ported to a React effect so it only runs in the
 * browser (never during SSR).
 *
 * Also pushes `{notify: 'newPageLoad'}` on every page after the first —
 * Taboola's loaded library recognizes this command (confirmed present in the
 * live loader.js bundle) as a signal to reset its internal "current page"
 * state before processing whatever is pushed next, which is what actually
 * makes the per-page `{article:'auto', url}` + widget declarations in
 * TaboolaRightRail/TaboolaInfiniteScrollWidgets/TaboolaWidget take effect on
 * a client-side navigation. Without it, those pushes land in the already-live
 * library but don't render anything — confirmed live that only a hard refresh
 * (which bootstraps loader.js fresh) worked before this was added.
 *
 * `pageKey` (e.g. a story slug or section slug) lets a caller force the
 * newPageLoad notify even when TaboolaLoader itself doesn't unmount/remount —
 * pass any value that changes per "page" so the effect re-fires reliably
 * regardless of whether the surrounding tree happens to remount.
 *
 * Gating: self-skips on Lighthouse/PageSpeed (navigator.webdriver + UA), the
 * same client-side guard IZootoInit uses, so callers can drop it into server
 * pages (BuilderPage, the article page) without threading server-side UA state.
 */
export default function TaboolaLoader(t0) {
  const $ = _c(5);
  const {
    pageType,
    pageKey
  } = t0;
  let t1;
  if ($[0] !== pageType) {
    t1 = () => {
      if (isLighthouseClient()) {
        return;
      }
      const w = window as unknown as {
        _taboola?: unknown[];
        performance?: Performance;
      };
      w._taboola = w._taboola || [];
      const alreadyLoadedThisSession = !!document.getElementById(LOADER_SCRIPT_ID);
      if (alreadyLoadedThisSession) {
        (w._taboola as unknown[]).push({
          notify: "newPageLoad"
        });
      }
      (w._taboola as unknown[]).push({
        [pageType]: "auto"
      });
      if (!alreadyLoadedThisSession) {
        registerTaboolaInjector(() => {
          if (document.getElementById(LOADER_SCRIPT_ID)) {
            return;
          }
          const e = document.createElement("script");
          const f = document.getElementsByTagName("script")[0];
          e.async = true;
          e.src = LOADER_SRC;
          e.id = LOADER_SCRIPT_ID;
          f?.parentNode?.insertBefore(e, f);
          if (w.performance && typeof w.performance.mark === "function") {
            w.performance.mark("tbl_ic");
          }
        });
      }
    };
    $[0] = pageType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pageKey || $[3] !== pageType) {
    t2 = [pageType, pageKey];
    $[2] = pageKey;
    $[3] = pageType;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
}